import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { ConfirmDialog } from '../components/Dialog';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import { lieferscheineUrl, lieferscheinVorlageUrl, type LieferscheinQuelle, type LieferscheinVorlage } from './AuftragDaten';
import {
  customerBody,
  drittlandArt,
  emptyRow,
  free,
  kundeWerteOf,
  LEERER_KUNDE,
  plusTage,
  positionBody,
  ratesFor,
  rowOf,
  summen,
  type KundeWerte,
  type PosRow,
} from './formular';
import { KundeWahl } from './KundeWahl';
import { useLeistungen } from './LeistungDaten';
import { PdfDialog } from './PdfDialog';
import { Positionen, SteuerHinweisFeld, Summen, useSteuerHinweis } from './Positionen';
import { darf, fehlerText } from './tarifHinweis';
import type { Kunde, Overview, Rate } from './types';
import { urlId } from './urlId';
import { useWaehrung, WaehrungFeld } from './WaehrungFeld';
import { waehrungAus } from './waehrung';
import './formular.css';
import './auftraege.css';
import './kunden.css';

// zurück zur Liste, aus der das Formular kam
const ZURUECK: Record<LieferscheinQuelle, string> = {
  auftrag: '/rechnung/auftraege',
  angebot: '/rechnung/angebote',
  rechnung: '/rechnung/rechnungen',
};
const QUELLE_TEXT: Record<LieferscheinQuelle, string> = { auftrag: 'Zu Auftrag', angebot: 'Zu Angebot', rechnung: 'Zur Rechnung' };

// Neuer Lieferschein: Kunde, Lieferort, Lieferdatum (Pflicht, heute vorbelegt), Positionen. Ohne Preise (Standard) nur
// Leistung, Menge und Einheit; mit Preisen wie die Auftragsbestätigung mit Summen und Hinweis bei 0 %. Keine Entwürfe.
// Aufruf mit ?auftrag=<id> (was noch zu liefern ist), ?angebot=<id> (Angebot ohne Auftragsbestätigung), ?rechnung=<id>
// (Lieferschein zur Rechnung) oder ?vorlage=<id> („Kopieren“ eines Lieferscheins). Währung und Kurs von der Quelle (die
// Währung dann fest), sonst die des Kunden; ohne Preise zugeklappt unter „Mehr“.
export function LieferscheinForm({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const navigate = useNavigate();
  const toast = useToast();
  const ku = data.absender.kleinunternehmer;
  const leistungen = useLeistungen(data.firm.id);
  const [params] = useSearchParams();
  // eine Quelle aus der Adresse (IDs nur als UUID, urlId.ts)
  const von: { art: LieferscheinQuelle | 'lieferschein'; id: string } | null = (() => {
    for (const art of ['auftrag', 'angebot', 'rechnung'] as const) {
      const id = urlId(params.get(art));
      if (id) return { art, id };
    }
    const id = urlId(params.get('vorlage'));
    return id ? { art: 'lieferschein' as const, id } : null;
  })();
  const [quelle, setQuelle] = useState<{ art: LieferscheinQuelle; id: string } | null>(null);
  const [kunde, setKundeState] = useState<KundeWerte>(LEERER_KUNDE);
  const [rows, setRows] = useState<PosRow[]>(() => [emptyRow()]);
  const drittland = drittlandArt(kunde);
  const steuerHinweis = useSteuerHinweis(rows, ku, data.hinweis0, drittland);
  const wae = useWaehrung({ firmId: data.firm.id, tarif: data.tarif, datum: data.today, preiseDa: rows.some((r) => r.price.trim() !== '') });
  const [mehr, setMehr] = useState(false);
  const [site, setSite] = useState('');
  const [lieferdatum, setLieferdatum] = useState(data.today);
  const [mitPreisen, setMitPreisen] = useState(false);
  const [bestellnummer, setBestellnummer] = useState('');
  const [note, setNote] = useState('');
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laden, setLaden] = useState(!!von);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  // Übersicht zum Zeitpunkt des Ladens (der Effekt unten läuft nur einmal je Adresse)
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const rates: Rate[] = ratesFor(ku);
  const total = summen(rows, ku).gross;
  const satz: Rate = drittland === 'firma' && !ku ? 0 : 19;
  // Kunde außerhalb der EU (Firma) gewählt oder nicht mehr: leere Positionen mit dem passenden Satz
  useEffect(() => {
    setRows((cur) => (cur.some((r) => free(r) && r.rate !== satz) ? cur.map((r) => (free(r) ? { ...r, rate: satz } : r)) : cur));
  }, [satz]);
  // Kunde aus der Liste gewählt: seine Währung (nicht bei einer Quelle, dort gilt deren Währung)
  const onPick = (k: Kunde | null) => !quelle && wae.setW(waehrungAus(k?.waehrung));
  // Lieferdatum wie auf dem Server: vom 1. Januar des Vorjahres bis ein Jahr im Voraus
  const datumMin = `${Number(data.today.slice(0, 4)) - 1}-01-01`;
  const datumMax = plusTage(data.today, 365);
  const vonKey = von ? `${von.art}:${von.id}` : '';

  // Vorbefüllt aus der Quelle (Kunde, Ort, Positionen; bei der AB nur, was noch offen ist) oder als Kopie
  useEffect(() => {
    if (!von) return;
    let live = true;
    api
      .get<LieferscheinVorlage>(lieferscheinVorlageUrl(dataRef.current.firm.id, von.art, von.id))
      .then(({ quelle: q, kopie, form, warnung }) => {
        if (!live) return;
        const kundeId = form.kundeId && dataRef.current.kunden.some((k) => k.id === form.kundeId) ? form.kundeId : null;
        setQuelle(form.quelle);
        setKundeState(kundeWerteOf(form.customer, kundeId));
        // Währung und Kurs der Quelle; Kopie: Währung des Lieferscheins, Kurs von heute
        wae.laden(waehrungAus(form.waehrung), form.kurs != null ? String(form.kurs) : null);
        setRows(form.items.length ? form.items.map((i) => rowOf({ ...i, price: i.price ?? undefined })) : [emptyRow()]);
        steuerHinweis.laden(form.items, form.steuerHinweis);
        setSite(form.site ?? '');
        setNote(form.note ?? '');
        setBestellnummer(form.bestellnummer ?? '');
        setLieferdatum(form.lieferdatum);
        setMitPreisen(form.mitPreisen);
        const text = q
          ? `${QUELLE_TEXT[q.art]} ${q.number}.${q.art === 'auftrag' && !warnung ? ' Die Mengen sind, was noch zu liefern ist.' : ''}`
          : `Kopie von Lieferschein ${kopie?.number ?? ''}. Bitte Mengen und Lieferdatum prüfen.`;
        setHinweis(`${text}${warnung ? ` ${warnung}` : ''}`);
      })
      .catch((err) => live && setError(errorText(err)))
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vonKey]);

  function body() {
    return {
      customer: customerBody(kunde, { xrechnung: false }),
      kundeId: kunde.kundeId,
      // ohne Preise: Preise nur, wenn lesbar (sie bleiben für die Rechnung daraus gespeichert)
      items: rows.map((r) => positionBody(r, ku, { ohnePreise: !mitPreisen })),
      site,
      note,
      bestellnummer,
      steuerHinweis: steuerHinweis.body,
      lieferdatum: lieferdatum || null,
      mitPreisen,
      quelle,
      ...wae.body,
    };
  }

  async function showPreview() {
    setError(null);
    setBusy(true);
    try {
      const blob = await api.blob(`${lieferscheineUrl(data.firm.id)}/vorschau`, body());
      setPreview(URL.createObjectURL(blob));
    } catch (err) {
      if (!(mitPreisen && steuerHinweis.amFeld(err))) setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  async function write() {
    setBusy(true);
    try {
      const res = await api.post<{ id: string; number: string }>(lieferscheineUrl(data.firm.id), body());
      // ein neuer Kunde steht jetzt in der Kundenliste
      await reload();
      toast(`Lieferschein ${res.number} geschrieben`);
      // gleich die PDF, mit „Per E-Mail“ und „Herunterladen“ (Auftraege.tsx)
      navigate('/rechnung/auftraege', { state: { show: res.id, art: 'lieferschein', number: res.number } });
    } catch (err) {
      setConfirm(false);
      if (!(mitPreisen && steuerHinweis.amFeld(err))) setError(fehlerText(err));
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!lieferdatum) {
      setError('Bitte ein Lieferdatum eintragen.');
      return;
    }
    setConfirm(true);
  }

  return (
    <form className="rt-card rf" onSubmit={submit} noValidate>
      <div className="rt-form-head">
        <h1>Neuer Lieferschein</h1>
        <Link to={von && von.art !== 'lieferschein' ? ZURUECK[von.art] : '/rechnung/auftraege'} className="btn small">
          Abbrechen
        </Link>
      </div>
      {hinweis && <p className="rt-vorlage">{hinweis}</p>}

      <h2>Kunde</h2>
      <KundeWahl kunden={data.kunden} items={data.items} today={data.today} value={kunde} onChange={setKundeState} onPick={onPick} />
      <div className="rt-grid">
        <label className="field rt-span4">
          <span>Lieferort</span>
          <input value={site} maxLength={200} placeholder="z. B. Baustelle Musterstraße 5" onChange={(e) => setSite(e.target.value)} />
        </label>
        <label className="field rt-span2">
          <span>Lieferdatum</span>
          <input type="date" required value={lieferdatum} min={datumMin} max={datumMax} onChange={(e) => setLieferdatum(e.target.value)} />
        </label>
        <label className="field rt-span2">
          <span>Bestellnummer des Kunden (optional)</span>
          <input value={bestellnummer} maxLength={100} autoComplete="off" onChange={(e) => setBestellnummer(e.target.value)} />
        </label>
      </div>

      <h2>Leistungen</h2>
      <label className="check">
        <input type="checkbox" checked={mitPreisen} onChange={(e) => setMitPreisen(e.target.checked)} />
        <span>Preise auf dem Lieferschein zeigen</span>
      </label>
      <Positionen
        rows={rows}
        onChange={setRows}
        ku={ku}
        rates={rates}
        leistungen={leistungen}
        leistungSpeichern={darf(data.tarif, 'kunden')}
        ohnePreise={!mitPreisen}
        w={wae.w}
        kurs={wae.kurs}
        satz={satz}
      />
      {mitPreisen ? (
        <>
          <SteuerHinweisFeld h={steuerHinweis} auf="dem Lieferschein" />
          <Summen rows={rows} ku={ku} titel="Gesamtbetrag" w={wae.w} kurs={wae.kurs} />
          <WaehrungFeld h={wae} fest={!!quelle} />
        </>
      ) : (
        // ohne Preise: Währung und Kurs nur für die Rechnung daraus, zugeklappt (offen, solange ein Kurs fehlt)
        <details className="kd-more au-mehr" open={mehr || (wae.fremd && !wae.kurs)} onToggle={(e) => setMehr((e.target as HTMLDetailsElement).open)}>
          <summary>
            Mehr <span className="muted">{wae.fremd ? `Währung ${wae.w}` : 'Währung'}</span>
          </summary>
          <WaehrungFeld h={wae} fest={!!quelle} />
        </details>
      )}

      <div className={`rt-grid${mitPreisen ? '' : ' au-hinweis'}`}>
        <label className="field rt-span4">
          <span>Hinweis</span>
          <textarea value={note} rows={2} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>

      {error && <div className="notice bad">{error}</div>}
      <div className="rt-actions rf-actions">
        <button type="button" className="btn" disabled={busy || laden} onClick={() => void showPreview()}>
          Vorschau
        </button>
        <button type="submit" className="btn primary" disabled={busy || laden}>
          Lieferschein schreiben
        </button>
      </div>

      <PdfDialog url={preview} title="Vorschau" onClose={() => setPreview(null)} />
      <ConfirmDialog
        open={confirm}
        title="Lieferschein jetzt schreiben?"
        text={`Er bekommt die nächste Lieferscheinnummer und das Datum von heute. Danach lässt er sich nicht mehr ändern, nur kopieren.${mitPreisen ? ` Gesamtbetrag: ${eurC(total / 100, wae.w)}.` : ''}`}
        confirmLabel="Ja, schreiben"
        busy={busy}
        onConfirm={() => void write()}
        onCancel={() => setConfirm(false)}
      />
    </form>
  );
}
